import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import PhoneAIMessageBubble from './PhoneAIMessageBubble';
import PhoneAIConfirmCard from './PhoneAIConfirmCard';
import PhoneAIWalletHandoff from './PhoneAIWalletHandoff';
import {
  askPhoneAI,
  getPhoneAIHistory,
  clearPhoneAIHistory,
  executePhoneAIAction,
  walletAckPhoneAI,
} from '../utils/phoneaiApi';
import './PhoneAIChat.css';

// PHONE chat panel. Phase 1: admin-only, read-only tools.
// Mounted from AdminDashboard.js as a fixed floating button + slide-out panel.
//
// Behaviour:
// - Closed by default. Click the floating button to open.
// - On first open in a session, auto-fires a "Briefing?" message so the admin
//   gets a 24h ops snapshot without typing (per the plan: "Reactive by default
//   + admin briefing on open").
// - Conversation history is loaded from /api/phoneai/history on open.
// - "New conversation" wipes the wallet's thread (keeps learned facts).
// - Pending-action banner is rendered when present, with a note that the
//   amber Confirm card lands in Phase 2.

const ADMIN_SUGGESTIONS = [
  "Show me my 24h briefing",
  "Any stuck orders right now?",
  "Check the operator wallet gas",
  "Remember: I prefer concise replies",
];

const USER_SUGGESTIONS = [
  "Show my recent orders",
  "What's my balance?",
  "What NFTs do I own?",
  "Recommend some SMM services",
];

const PhoneAIChat = ({ persona = 'admin' }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, toolName, toolsUsed, createdAt}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingWalletTx, setPendingWalletTx] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [walletAcking, setWalletAcking] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const briefingFiredRef = useRef(false);
  const bodyRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      requestAnimationFrame(() => {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      });
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const msgs = await getPhoneAIHistory();
      setMessages(msgs);
      setHistoryLoaded(true);
    } catch (e) {
      setHistoryLoaded(true);
    }
  }, []);

  const send = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || sending) return;
    setSending(true);
    setInput('');
    setPendingAction(null);
    setPendingWalletTx(null);
    // Optimistic user bubble
    setMessages((prev) => [...prev, { role: 'user', content: trimmed, createdAt: new Date().toISOString() }]);

    try {
      const res = await askPhoneAI(trimmed);
      const assistant = {
        role: 'assistant',
        content: res?.assistant_reply || '',
        toolsUsed: res?.tools_used || [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistant]);
      if (res?.pending_action) setPendingAction(res.pending_action);
      if (res?.pending_wallet_tx) setPendingWalletTx(res.pending_wallet_tx);
      if (res?.error) {
        // surface model errors quietly — assistant reply already explains
        console.warn('PhoneAI error:', res.error);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'PHONE failed to respond.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: msg, createdAt: new Date().toISOString() },
      ]);
      if (e?.response?.status === 429) {
        toast.warn(msg);
      }
    } finally {
      setSending(false);
    }
  }, [sending]);

  const handleConfirm = useCallback(async (token) => {
    if (!token || confirming) return;
    setConfirming(true);
    try {
      const res = await executePhoneAIAction(token);
      const assistant = {
        role: 'assistant',
        content: res?.assistant_reply || 'Done.',
        toolsUsed: [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistant]);
      if (res?.error) toast.warn(res.error);
      else toast.success('Action confirmed.');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Action failed.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Action failed: ${msg}`, createdAt: new Date().toISOString() },
      ]);
      toast.error(msg);
    } finally {
      setConfirming(false);
      setPendingAction(null);
    }
  }, [confirming]);

  const handleCancelPending = useCallback(() => {
    // Token expires server-side; we just drop it client-side.
    setPendingAction(null);
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: 'OK — cancelled. Nothing was executed.', createdAt: new Date().toISOString() },
    ]);
  }, []);

  const handleWalletSigned = useCallback(async (token, txHash) => {
    if (!token || !txHash || walletAcking) return;
    setWalletAcking(true);
    try {
      const res = await walletAckPhoneAI(token, txHash);
      const assistant = {
        role: 'assistant',
        content: res?.assistant_reply || `Transaction ${txHash.slice(0, 10)}…${txHash.slice(-6)} recorded.`,
        toolsUsed: [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistant]);
      toast.success('Signed in wallet ✓');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to record the signed tx.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Tx broadcast but PHONE couldn't record it: ${msg}`, createdAt: new Date().toISOString() },
      ]);
      toast.error(msg);
    } finally {
      setWalletAcking(false);
      setPendingWalletTx(null);
    }
  }, [walletAcking]);

  const handleWalletCancel = useCallback(() => {
    setPendingWalletTx(null);
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: 'OK — cancelled. Nothing was sent to your wallet.', createdAt: new Date().toISOString() },
    ]);
  }, []);

  const handleClear = useCallback(async () => {
    if (!window.confirm('Clear this conversation? (Learned facts are kept.)')) return;
    try {
      await clearPhoneAIHistory();
      setMessages([]);
      setPendingAction(null);
      setPendingWalletTx(null);
      briefingFiredRef.current = false;
      toast.success('PHONE conversation cleared.');
    } catch (_) {
      toast.error('Failed to clear history.');
    }
  }, []);

  // Load history when panel first opens.
  useEffect(() => {
    if (!open || historyLoaded) return;
    loadHistory();
  }, [open, historyLoaded, loadHistory]);

  // Auto-fire admin briefing on first open in this session, but only if the
  // wallet has no prior history (so we don't re-greet returning sessions).
  useEffect(() => {
    if (!open) return;
    if (!historyLoaded) return;
    if (briefingFiredRef.current) return;
    if (persona !== 'admin') return;
    if (messages.length > 0) return; // returning session — skip
    briefingFiredRef.current = true;
    send("Give me my 24h briefing — anything that needs my attention right now?");
  }, [open, historyLoaded, persona, messages.length, send]);

  // Keep scrolled to the latest message.
  useEffect(() => {
    scrollToBottom();
  }, [messages, pendingAction, pendingWalletTx, scrollToBottom]);

  if (!open) {
    return (
      <button className="phoneai-fab" onClick={() => setOpen(true)} title="Ask PHONE">
        PHONE
      </button>
    );
  }

  return (
    <div className="phoneai-panel" role="dialog" aria-label="PHONE assistant">
      <div className="phoneai-header">
        <div>
          <div className="phoneai-header-title">PHONE</div>
          <div className="phoneai-header-sub">{persona === 'admin' ? 'Admin · ops' : 'Your assistant'}</div>
        </div>
        <div className="phoneai-header-actions">
          <button className="phoneai-header-btn" onClick={handleClear} title="Clear conversation">New</button>
          <button className="phoneai-header-btn" onClick={() => setOpen(false)} title="Close">✕</button>
        </div>
      </div>

      <div className="phoneai-body" ref={bodyRef}>
        {messages.length === 0 && !sending && (
          <div className="phoneai-empty">
            Ask PHONE anything about your platform.
            <div className="phoneai-suggestions">
              {(persona === 'admin' ? ADMIN_SUGGESTIONS : USER_SUGGESTIONS).map((s) => (
                <button key={s} className="phoneai-suggestion" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <PhoneAIMessageBubble
            key={`${m.createdAt || i}-${i}`}
            role={m.role}
            content={m.content}
            toolName={m.toolName}
            toolsUsed={m.toolsUsed}
          />
        ))}
        {pendingAction && (
          <PhoneAIConfirmCard
            pendingAction={pendingAction}
            onConfirm={handleConfirm}
            onCancel={handleCancelPending}
            running={confirming}
          />
        )}
        {pendingWalletTx && (
          <PhoneAIWalletHandoff
            pendingWalletTx={pendingWalletTx}
            onSigned={handleWalletSigned}
            onCancel={handleWalletCancel}
            running={walletAcking}
          />
        )}
        {sending && (
          <div className="phoneai-row phoneai-row--assistant">
            <div className="phoneai-bubble phoneai-bubble--assistant" style={{ opacity: 0.7 }}>
              PHONE is thinking…
            </div>
          </div>
        )}
      </div>

      <div className="phoneai-input-row">
        <input
          className="phoneai-input"
          value={input}
          placeholder="Ask PHONE…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          disabled={sending}
          maxLength={4000}
        />
        <button className="phoneai-send" onClick={() => send(input)} disabled={sending || !input.trim()}>
          Send
        </button>
      </div>

      <div className="phoneai-footer">Powered by AI — review before confirming any action.</div>
    </div>
  );
};

export default PhoneAIChat;
